import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonPropsDrawer } from '@celebration/react/ClbDrawer/ClbDrawer.types';
import Drawer from '../../../../common/components/drawer';
import ICarriersFilter from './interfaces/ICarriersFilters';
import ICarriersFiltersModel from '../../interfaces/ICarriersFiltersModel';
import Input from '../../../../common/components/input';
import { MASKTYPE } from '../../../../common/components/input/input-constants';
import emptyActiveCarrierFilterModel from './constants';
import placeholderPatterns from '../../../../common/components/input/placeholder-patterns';
import './carriers-filters.scss';
import getRegionsData from '../../../../common/constants/multi-select-data';
import SelectAutocomplete from '../../../../common/components/select-autocomplete';

function CarriersFilter(props: Readonly<ICarriersFilter>) {
    const { t } = useTranslation();
    const {
        currentFilters,
        onApplyFilter,
        isShowingFilters,
        toggleShowFilters,
    } = props;
    currentFilters.regions = currentFilters.regions ?? [];

    const [filtersModel, setFiltersModel] =
        useState<ICarriersFiltersModel>(currentFilters);

    const onClearFilters = useCallback(() => {
        setFiltersModel({
            ...emptyActiveCarrierFilterModel,
            description: currentFilters.description,
        });
    }, [currentFilters, setFiltersModel]);

    const clearButton: ButtonPropsDrawer = {
        key: 'filter-clear-button',
        id: 'filter-clear-button',
        label: t('Limpar'),
        disabled: false,
        loading: false,
        title: t('LimparFiltros'),
        onClick: () => onClearFilters(),
        'data-testid': 'filter-clear-button',
        role: 'button',
        size: 'md',
        iconPosition: 'none',
        styleType: 'secondary',
    };

    const submitButton: ButtonPropsDrawer = {
        key: 'filter-submit-button',
        id: 'filter-submit-button',
        label: t('Filtrar'),
        disabled: false,
        loading: false,
        title: t('FiltrarTransportadoras'),
        onClick: () => onApplyFilter(filtersModel),
        'data-testid': 'filter-submit-button',
        role: 'button',
        size: 'md',
        iconPosition: 'none',
        styleType: 'primary',
    };

    const buttonsList: [ButtonPropsDrawer, ButtonPropsDrawer?] = [
        clearButton,
        submitButton,
    ];

    return (
        <Drawer
            title={t('FiltrarTransportadora')}
            open={isShowingFilters}
            onClose={toggleShowFilters}
            buttonsFooter={buttonsList}
            data-testid="carriers-filters"
        >
            <Input
                label={t('NomeTransportadora')}
                className="carrier-name"
                placeholder={t('NomeFantasia')}
                value={filtersModel.carrierName}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        carrierName: e.target.value,
                    });
                }}
                dataTestId="carriers-filters-carrier-name"
            />

            <SelectAutocomplete
                mode="multiple"
                label={t('RegioesAtendidas')}
                value={filtersModel.regions}
                onChange={(e: any) => {
                    setFiltersModel({ ...filtersModel, regions: e });
                }}
                placeholder={t('RegioesAtendidas')}
                dataTestId="carriers-filters-regions"
                options={getRegionsData(t)}
                getPopupContainer={node =>
                    (node.parentNode as HTMLElement) ?? document.body
                }
            />

            <Input
                label="CNPJ"
                mask={MASKTYPE.CNPJ}
                value={filtersModel.cnpj}
                placeholder={placeholderPatterns.CNPJ}
                onChange={(e: any) => {
                    setFiltersModel({ ...filtersModel, cnpj: e.target.value });
                }}
                dataTestId="carriers-filters-cnpj"
            />

            <Input
                label={t('CodigoSap')}
                value={filtersModel.sapCode}
                placeholder={placeholderPatterns.SAP_S4}
                maxlength={10}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        sapCode: e.target.value,
                    });
                }}
                dataTestId="carriers-filters-sap-code"
            />

            <Input
                label={t('CodigoTms')}
                value={filtersModel.tmsCode}
                placeholder={placeholderPatterns.TMS}
                maxlength={10}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        tmsCode: e.target.value,
                    });
                }}
                dataTestId="carriers-filters-tms-code"
            />

            <Input
                label={t('CodigoEcc')}
                value={filtersModel.eccCode}
                placeholder={placeholderPatterns.SAP_ECC}
                maxlength={6}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        eccCode: e.target.value,
                    });
                }}
                dataTestId="carriers-filters-ecc-code"
            />
        </Drawer>
    );
}

export default CarriersFilter;
